import { useState } from 'react';
import { sdk } from '@/services/sdk';
import ApperIcon from '@/components/ApperIcon';
import { Button } from '@/components/ui/button';
import { Busy } from '@/components/kit';
import { committed, nowIso } from '@/shared/data';
import { isOneTimeOrder, money, renewalPrice } from '@/shared/pricing';
import { formatRelativeTime } from '@/utils/date';
import { useT } from '@/shared/i18n';
import { A } from '@/shared/locales/account/keys';

/** Lets a customer ask for their next invoice. The team clears the request when the term is extended. */
export default function RenewButton({ order, onDone, size = 'sm' }) {
  const { t } = useT();
  const [busy, setBusy] = useState(false);

  if (!order || !['Active', 'Trial'].includes(order.status_c)) return null;
  // Paid once: nothing to renew.
  if (isOneTimeOrder(order)) return null;

  if (order.renew_requested_at_c) {
    return (
      <span className="inline-flex items-center gap-1.5 text-xs text-muted-foreground">
        <ApperIcon name="Check" size={14} className="text-success" />
        {t(A.renewReq, { when: formatRelativeTime(order.renew_requested_at_c) })}
      </span>
    );
  }

  const isTrial = order.status_c === 'Trial';
  const price = isTrial ? Number(order.price_c || 0) : renewalPrice(order);

  async function request() {
    setBusy(true);
    try {
      const res = await sdk.table('orders_c').update({ Id: order.Id, renew_requested_at_c: nowIso() });
      if (committed(res, t(A.renewDone))) onDone?.();
    } finally {
      setBusy(false);
    }
  }

  return (
    <Button size={size} onClick={request} disabled={busy}>
      <Busy
        busy={busy}
        busyLabel={t(A.requesting)}
        label={
          <>
            <ApperIcon name="RefreshCw" size={16} />
            {isTrial ? t(A.keepLine) : t(A.reqRenew)} · {money(price)}
          </>
        }
      />
    </Button>
  );
}
